import { useMemo } from "react";

import { Pt } from "../../utils/apolloRenderer";

/**
 * 교차로 신호등(z-up). 맵 signal 경계의 중심에 3색 신호등을 세우고,
 * 현재 인지 신호색(frame.signalColor)에 해당하는 등을 점등한다.
 * 신호등이 자차에서 80~100m 떨어져 있어, 원거리 가시성을 위해 크게 + 발광 빔을 추가.
 * (signalColor는 자차 기준 단일 값이라 모든 신호등에 동일 적용 — 근사 표시.)
 */

const centroid = (poly: Pt[]): [number, number] => {
    let x = 0, y = 0;
    poly.forEach((p) => { x += p[0]; y += p[1]; });
    const n = poly.length || 1;
    return [x / n, y / n];
};

const litColorOf = (c: string): string | null =>
    c === "RED" ? "#ff3b30" : c === "YELLOW" ? "#ffcc00" : (c === "GREEN" || c === "FLASHING_GREEN") ? "#39d353" : null;

function Light({ z, color, on }: { z: number; color: string; on: boolean }) {
    return (
        <mesh position={[0, 0.28, z]}>
            <sphereGeometry args={[0.34, 20, 20]} />
            <meshBasicMaterial color={on ? color : "#20242a"} />
        </mesh>
    );
}

export default function TrafficLights({ signals, color, visible = true }: { signals?: Pt[][]; color?: string | null; visible?: boolean }) {
    const centers = useMemo(() => (signals || []).filter((s) => s.length >= 1).map(centroid), [signals]);
    if (!visible || centers.length === 0) return null;
    const c = color || "";
    const lit = litColorOf(c);
    return (
        <group>
            {centers.map((p, i) => (
                <group key={i} position={[p[0], p[1], 0]}>
                    {/* 기둥 */}
                    <mesh position={[0, 0, 2.3]}>
                        <boxGeometry args={[0.22, 0.22, 4.6]} />
                        <meshBasicMaterial color="#2b2f36" />
                    </mesh>
                    {/* 하우징 */}
                    <mesh position={[0, 0.12, 5.4]}>
                        <boxGeometry args={[0.95, 0.55, 2.5]} />
                        <meshBasicMaterial color="#0d0f12" />
                    </mesh>
                    {/* 3색 등(적/황/녹) */}
                    <Light z={6.1} color="#ff3b30" on={c === "RED"} />
                    <Light z={5.4} color="#ffcc00" on={c === "YELLOW"} />
                    <Light z={4.7} color="#39d353" on={c === "GREEN" || c === "FLASHING_GREEN"} />
                    {/* 원거리 가시성: 현재 색 발광 빔 + 상단 비콘 */}
                    {lit && (
                        <>
                            <mesh position={[0, 0, 10]}>
                                <boxGeometry args={[0.5, 0.5, 11]} />
                                <meshBasicMaterial color={lit} transparent opacity={0.3} depthWrite={false} />
                            </mesh>
                            <mesh position={[0, 0, 7.2]}>
                                <sphereGeometry args={[0.7, 20, 20]} />
                                <meshBasicMaterial color={lit} transparent opacity={0.9} depthWrite={false} />
                            </mesh>
                        </>
                    )}
                </group>
            ))}
        </group>
    );
}
